// src/automation/components/AutomationCookiesTab.js
//
// Admin "Auth" tab — read-only status of the seeded service cookies in S3.
//
// Live Midway federation does not work from Lambda, so the automations depend
// on service cookies (grafana_session, and the Paragon rcc-at/rcc-rt/rcc-state
// set) being seeded into S3 from a trusted host. This tab shows, per service,
// whether the cookie is valid, until when, and how long is left — so you know
// when a re-seed is due. Data comes from the read-only `cookie_status` command
// (no task run, no federation).

import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, RefreshCw, KeyRound, Clock, Save } from "lucide-react";
import { getCookieStatus, getGlobalConfig, saveGlobalConfig } from "../api/automation_data";
import { CollapsibleSection, Toast } from "wbr/components/admin/AdminShared";
import { useUser } from "shared/context/UserContext";

// The backend only answers these commands for owners (env ADMIN_LOGINS), so
// every call carries the signed-in login.
const useLogin = () => useUser()?.userId || "";

const SERVICE_LABEL = {
  grafana: "Grafana",
  rcc: "RCC / Paragon",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso.replace(" UTC", "Z").replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function fmtRemaining(mins) {
  if (mins == null) return "—";
  if (mins <= 0) return "expired";
  if (mins < 60) return `${mins} min left`;
  const hrs = mins / 60;
  if (hrs < 48) return `${hrs.toFixed(1)} hr left`;
  return `${(hrs / 24).toFixed(1)} d left`;
}

function Badge({ valid }) {
  return valid ? (
    <span className="auto-status auto-status--ok"><CheckCircle2 size={13} /> Valid</span>
  ) : (
    <span className="auto-status auto-status--err"><XCircle size={13} /> Expired</span>
  );
}

export default function AutomationCookiesTab() {
  const login = useLogin();
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["automationCookieStatus", login],
    queryFn: () => getCookieStatus(login),
    enabled: !!login,
    staleTime: 60 * 1000,
    // Poll every 5 min, and only while this tab is actually in the foreground —
    // no background polling when the browser/tab isn't focused. There's also a
    // manual Refresh button for an on-demand check.
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const cookies = data?.cookies || [];
  const checkedAt = data?.checked_at;

  return (
    <div className="admin-tab-content">
      <CollapsibleSection
        title="Service Cookies"
        subtitle="Seeded auth cookies the automations run on (read-only)"
        actions={
          <button
            className="admin-btn admin-btn--sm admin-btn--outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw size={12} /> {isFetching ? "Checking…" : "Refresh"}
          </button>
        }
      >
        {cookies.length === 0 ? (
          <div className="admin-empty-state admin-empty-state--sm">
            <p>{data?.error ? `Could not load cookie status: ${data.error}` : "No cookie status available."}</p>
            {data?.error && <p style={{ fontSize: 12, opacity: 0.75 }}>Signed in as: {login || "(unknown)"}</p>}
          </div>
        ) : (
          <div className="auto-cookie-grid">
            {cookies.map((c) => (
              <div
                className={`auto-cookie-card ${c.valid ? "" : "auto-cookie-card--bad"}`}
                key={c.service}
              >
                <div className="auto-cookie-card__head">
                  <span className="auto-cookie-card__title">
                    <KeyRound size={15} /> {SERVICE_LABEL[c.service] || c.service}
                  </span>
                  <Badge valid={c.valid} />
                </div>
                <div className="auto-cookie-card__meta">
                  <div className="auto-cookie-kv">
                    <span>Valid until</span>
                    <b>{fmtWhen(c.valid_until)}</b>
                  </div>
                  <div className="auto-cookie-kv">
                    <span><Clock size={11} /> Remaining</span>
                    <b>{fmtRemaining(c.remaining_minutes)}</b>
                  </div>
                  <div className="auto-cookie-kv">
                    <span>S3 key</span>
                    <code>{c.s3_key}</code>
                  </div>
                  {!c.valid && (
                    <div className="auto-cookie-reason">
                      {c.reason === "missing"
                        ? "Cookie not seeded yet."
                        : c.reason === "expired"
                        ? "Cookie expired — re-seed from a trusted host."
                        : c.reason}
                    </div>
                  )}
                </div>
                {Array.isArray(c.cookies) && c.cookies.length > 1 && (
                  <div className="auto-cookie-card__sub">
                    {c.cookies.map((sub) => (
                      <span
                        key={sub.name}
                        className={`auto-cookie-chip ${sub.alive ? "" : "auto-cookie-chip--bad"}`}
                        title={`${sub.name}: ${fmtRemaining(sub.remaining_minutes)}`}
                      >
                        {sub.name} {sub.alive ? "✓" : "✕"}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="admin-field__hint" style={{ marginTop: 10 }}>
          Cookies are derived on a trusted host (run <code>scripts/seed_service_cookies.py</code>)
          and uploaded to S3. Grafana (<code>grafana_session</code>) lasts ~24h; RCC/Paragon
          (<code>rcc-at</code> + <code>rcc-rt</code> + <code>rcc-state</code>) lasts ~8h. When a
          cookie expires the matching tasks fail and an alert is posted to the Slack alert channel.
          {checkedAt && <> Last checked {fmtWhen(checkedAt)}.</>}
        </p>
      </CollapsibleSection>

      <GlobalConfigEditor />
    </div>
  );
}

// ── Notifications (test + alert destinations) ───────────────────────────────
// One Slack channel + one email serve BOTH test runs and operational alerts.
// Edits global.json ses.test_email + slack.test_channel_id via a simple form
// (merges into the full config so nothing else is touched). A collapsible raw
// JSON editor is kept underneath for advanced edits.
function GlobalConfigEditor() {
  const login = useLogin();
  const [cfg, setCfg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [testChannel, setTestChannel] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Slack tokens. We don't want to re-display a live secret, so each token has:
  //  - a "current" marker (whether one is stored) and
  //  - an input that is ONLY written if the admin types a new value.
  const [botTokenCur, setBotTokenCur] = useState("");   // existing (maybe masked ${ENV})
  const [appTokenCur, setAppTokenCur] = useState("");
  const [botTokenNew, setBotTokenNew] = useState("");   // new value to write (blank = keep)
  const [appTokenNew, setAppTokenNew] = useState("");
  const [editBot, setEditBot] = useState(false);
  const [editApp, setEditApp] = useState(false);

  // Raw JSON editor (advanced).
  const [rawText, setRawText] = useState("");
  const [rawErr, setRawErr] = useState(null);
  const [rawSaving, setRawSaving] = useState(false);

  const load = () => {
    setLoaded(false);
    setLoadErr(null);
    getGlobalConfig(login)
      .then((c) => {
        if (!c) { setLoadErr("Could not load global config."); return; }
        setCfg(c);
        setTestChannel((c.slack && c.slack.test_channel_id) || "");
        setTestEmail((c.ses && c.ses.test_email) || "");
        setBotTokenCur((c.slack && c.slack.bot_token) || "");
        setAppTokenCur((c.slack && c.slack.app_token) || "");
        setBotTokenNew(""); setAppTokenNew(""); setEditBot(false); setEditApp(false);
        setRawText(JSON.stringify(c, null, 2));
      })
      .catch((e) => setLoadErr(e?.message || "Load failed"))
      .finally(() => setLoaded(true));
  };
  // Re-load if the signed-in login resolves after first render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [login]);

  const saveFields = async () => {
    if (!cfg) return;
    const slack = { ...(cfg.slack || {}), test_channel_id: testChannel.trim() };
    // Only overwrite a token when the admin typed a new one.
    if (editBot && botTokenNew.trim()) slack.bot_token = botTokenNew.trim();
    if (editApp && appTokenNew.trim()) slack.app_token = appTokenNew.trim();
    const next = {
      ...cfg,
      slack,
      ses: { ...(cfg.ses || {}), test_email: testEmail.trim() },
    };
    setSaving(true);
    try {
      await saveGlobalConfig(next, login);
      setCfg(next);
      setBotTokenCur(slack.bot_token || "");
      setAppTokenCur(slack.app_token || "");
      setBotTokenNew(""); setAppTokenNew(""); setEditBot(false); setEditApp(false);
      setRawText(JSON.stringify(next, null, 2));
      setToast({ message: "Settings saved", type: "success" });
    } catch (e) {
      setToast({ message: e.message || "Save failed", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const tokenStatus = (v) => {
    if (!v) return "not set";
    if (v.startsWith("${")) return "from env var";
    return "set (" + v.slice(0, 6) + "…" + v.slice(-3) + ")";
  };

  const onRawChange = (v) => {
    setRawText(v);
    try { JSON.parse(v); setRawErr(null); } catch (e) { setRawErr(e.message); }
  };
  const saveRaw = async () => {
    let parsed;
    try { parsed = JSON.parse(rawText); } catch { setToast({ message: "Invalid JSON", type: "error" }); return; }
    setRawSaving(true);
    try {
      await saveGlobalConfig(parsed, login);
      setCfg(parsed);
      setTestChannel((parsed.slack && parsed.slack.test_channel_id) || "");
      setTestEmail((parsed.ses && parsed.ses.test_email) || "");
      setToast({ message: "Global config saved", type: "success" });
    } catch (e) {
      setToast({ message: e.message || "Save failed", type: "error" });
    } finally {
      setRawSaving(false);
    }
  };

  return (
    <>
      <CollapsibleSection
        title="Notifications"
        subtitle="Where Test runs and alerts go — one Slack channel + one email for both"
        actions={
          <button
            className="admin-btn admin-btn--sm admin-btn--primary"
            onClick={saveFields}
            disabled={saving || !loaded || !cfg}
          >
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        }
      >
        {loadErr ? (
          <div className="admin-empty-state admin-empty-state--sm"><p>{loadErr}</p></div>
        ) : !loaded ? (
          <p>Loading…</p>
        ) : (
          <div className="auto-notif-form">
            <label className="auto-notif-field">
              <span className="auto-notif-label">Test / Alert Slack channel ID</span>
              <input
                className="admin-input"
                value={testChannel}
                onChange={(e) => setTestChannel(e.target.value)}
                placeholder="e.g. C09D31R9EQZ"
              />
              <span className="admin-field__hint">Test runs and cookie-expiry alerts both post here.</span>
            </label>
            <label className="auto-notif-field">
              <span className="auto-notif-label">Test / Alert email</span>
              <input
                className="admin-input"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="e.g. cia-pulse-test@amazon.com"
              />
              <span className="admin-field__hint">

                Used for email alerts. Test runs from the console go to whoever runs them
                (their own @amazon.com inbox); this address is only the fallback when a test has no login.
              </span>
            </label>

            <div className="auto-notif-divider" />

            <label className="auto-notif-field">
              <span className="auto-notif-label">
                Slack bot token <span className="auto-token-status">· {tokenStatus(botTokenCur)}</span>
              </span>
              {editBot ? (
                <input
                  className="admin-input"
                  type="password"
                  value={botTokenNew}
                  onChange={(e) => setBotTokenNew(e.target.value)}
                  placeholder="xoxb-…"
                  autoComplete="off"
                />
              ) : (
                <button className="admin-btn admin-btn--sm admin-btn--outline" type="button" onClick={() => setEditBot(true)}>
                  {botTokenCur ? "Replace token" : "Set token"}
                </button>
              )}
              <span className="admin-field__hint">
                The <code>xoxb-…</code> Bot User OAuth token. Needs <code>files:write</code> + <code>chat:write</code>.
              </span>
            </label>

            <label className="auto-notif-field">
              <span className="auto-notif-label">
                Slack app token <span className="auto-token-status">· {tokenStatus(appTokenCur)}</span>
              </span>
              {editApp ? (
                <input
                  className="admin-input"
                  type="password"
                  value={appTokenNew}
                  onChange={(e) => setAppTokenNew(e.target.value)}
                  placeholder="xapp-…"
                  autoComplete="off"
                />
              ) : (
                <button className="admin-btn admin-btn--sm admin-btn--outline" type="button" onClick={() => setEditApp(true)}>
                  {appTokenCur ? "Replace token" : "Set token"}
                </button>
              )}
              <span className="admin-field__hint">
                Optional (<code>xapp-…</code>). Not used for posting — stored for completeness.
              </span>
            </label>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Global Config (raw JSON)"
        subtitle="Advanced: full global.json. Secrets show as ${ENV} tokens — leave as-is."
        defaultOpen={false}
        actions={
          <>
            <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={load} disabled={!loaded}>
              <RefreshCw size={12} /> Reload
            </button>
            <button
              className="admin-btn admin-btn--sm admin-btn--primary"
              onClick={saveRaw}
              disabled={rawSaving || !!rawErr || !loaded}
            >
              <Save size={12} /> {rawSaving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        {!loaded ? (
          <p>Loading…</p>
        ) : (
          <>
            <textarea
              className="auto-eb-textarea"
              style={{ width: "100%", minHeight: 300, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
              value={rawText}
              onChange={(e) => onRawChange(e.target.value)}
              spellCheck={false}
            />
            {rawErr && <p className="admin-field__hint" style={{ color: "#c0392b" }}>Invalid JSON: {rawErr}</p>}
          </>
        )}
      </CollapsibleSection>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}
